import { DeviceDetection } from "oc-commons/DeviceDetection";
// @ts-ignore
import * as Maths3 from "oc-threejs/Maths3";

export interface IRendererSettings {
  antialias: boolean;
  alpha: boolean;
  clearColor: {
    color: number;
    alpha: number;
  };
  sortObjects: boolean;
  toneMapping: string;
  exposure: number;
  logarithmicDepthBuffer: boolean;
}

export interface IRaycasterSettings {
  linePrecision: number;
  coneTestPixel: number;
}

export interface IDragDropSettings {
  clickMsTreshold: number;
  clickPxTreshold: number;
  cursorChangesOverDraggables: boolean;
  radiusSnapStart: number;
  radiusSnapEnd: number;
  radiusSnapStep: number;
  restraintsDebugColor: number;
  snapsDebugColor: number;
  dragOpacity: number;
}

export interface IRulerSettings {
  scale: number;
  fixed: number;
  unit: string;
  mInUnit: number;
}

export interface IArSettings {
  possible: boolean;
  autoStartInApp: boolean;
  hiddenObjectNames: string[];
  debug: boolean;
}

export interface IVector3Simple {
  x: number;
  y: number;
  z: number;
}

export interface ICameraSettings {
  fov: number;
  near: number;
  far: number;
  initialZoomFaktor: number;
  zoomSpeed: number;
  rotateSpeed: number;
  enableDamping: boolean;
  dampingFactor: number;
  target: IVector3Simple;
  position: IVector3Simple;
  disablePan: boolean;
  bindToYPlaneWithHeight: boolean;
  distance: {
    min: number;
    max: number;
  };
  polarAngle: {
    min: number;
    max: number;
    locked: boolean;
  };
  azimuthAngle: {
    min: number;
    max: number;
  };
  minCameraTargetPosition: IVector3Simple;
  maxCameraTargetPosition: IVector3Simple;
  minCameraPosition: IVector3Simple;
  maxCameraPosition: IVector3Simple;
}

export interface IConnectorMarkersSettings {
  alpha: number;
  speed: number;
  ab: number;
  tori: number;
  radius: number;
  tube: number;
  radialSegments: number;
  tubularSegments: number;
}

export interface IConnectorsSettings {
  size: number;
  anchorPlane: any; // Maths3.plane is complex object
  anchorsAreGroundedDistance: number;
  distanceWhereForceIs0: number;
  distanceWhereForceIs1: number;
  distanceWhereForcePulseIs0: number;
  distanceWhereForcePulseIs1: number;
  PulseFactor: number;
  PulseGroudLine: number;
  openingAngle: number;
  openingAngleForce: number;
  openingAngleWhereForceIs1: number;
  forceShuffleSteps: number;
  snapDistance: number;
  markers: IConnectorMarkersSettings;
  box: {
    animate: boolean;
    minDistanceTreshold: number;
  };
}

export interface IShadowsSettings {
  enabled: boolean;
  shadowMapType: string;
}

export interface IPrintDesignerSettings {
  hideControls: boolean;
  font: {
    scale: number;
    color: string;
  };
  qualityThreshold: {
    lowDPI: number;
    highDPI: number;
  };
}

export interface ISaoPassSettings {
  saoBias: number;
  saoIntensity: number;
  saoScale: number;
  saoKernelRadius: number;
  saoMinResolution: number;
  saoBlur: boolean;
  saoBlurRadius: number;
  saoBlurStdDev: number;
  saoBlurDepthCutoff: number;
}

export interface IDefaultStageSettings {
  renderer: IRendererSettings;
  ratioFactor: number;
  showFramesPerSecondInfo: boolean;
  showBoundingBoxRuler: boolean;
  showDebugObjects: boolean;
  showDebugNotANumberInSceneError: boolean;
  showGroundPlane: boolean;
  sky_radius: number;
  raycaster: IRaycasterSettings;
  dragDrop: IDragDropSettings;
  ruler: IRulerSettings;
  lowPerformanceDevice: boolean;
  ar: IArSettings;
  camera: ICameraSettings;
  connectors: IConnectorsSettings;
  shadows: IShadowsSettings;
  printDesigner: IPrintDesignerSettings;
  saoPass: ISaoPassSettings;
  rendererBackgroundImage: boolean;
  lightSettingsName: string;
}

export const DefaultStageSettings: IDefaultStageSettings = {
  renderer: {
    // Perfomance Cost!
    antialias: false,
    // Performance Cost! Set only true to export transparent png
    alpha: false,
    clearColor: {
      color: 0xeeeeee,
      alpha: 1,
    },
    sortObjects: true,
    toneMapping: "LinearToneMapping",
    exposure: 1,
    // Perfomance Cost!
    logarithmicDepthBuffer: false,
  },
  ratioFactor: 1,
  showFramesPerSecondInfo: false,
  showBoundingBoxRuler: true,
  showDebugObjects: false,
  showDebugNotANumberInSceneError: true,
  showGroundPlane: false,
  sky_radius: 10000,

  raycaster: {
    // welchen Raycast-Radius hat eine Linie? Ist leider ein globales Property in ThreeJS, wäre besser eine
    // Eigenschaft der Linie
    linePrecision: 0.01,
    // coneTestPixel lässt den Raycast-Test vom InteractionHandler breiter werden, um auch sehr dünne
    // Objekte treffen zu können, vor allem bei Touch-Bedienung interessant. Wenn der Wert 0 ist,
    // dann findet nur ein normaler, exakter Raycast-Test statt.
    coneTestPixel: 0,
  },

  dragDrop: {
    clickMsTreshold: 600,
    clickPxTreshold: 12,
    cursorChangesOverDraggables: true,
    radiusSnapStart: 0.2,
    radiusSnapEnd: 0.6,
    radiusSnapStep: 0.1,
    restraintsDebugColor: 0x707070,
    snapsDebugColor: 0xffffff,
    dragOpacity: 0.8,
  },

  ruler: {
    scale: 1.0,
    fixed: 2,
    unit: "m",
    mInUnit: 1.0,
  },

  lowPerformanceDevice: DeviceDetection.isLowPerformanceDevice(),
  ar: {
    possible: false,
    autoStartInApp: true,
    hiddenObjectNames: [],
    debug: false,
  },
  camera: {
    fov: 45,
    near: 0.01,
    far: 10000,
    initialZoomFaktor: 1,
    zoomSpeed: 1.0,
    rotateSpeed: 1.0,
    enableDamping: false,
    dampingFactor: 0.25,
    target: {
      x: 0,
      y: 0,
      z: 0,
    },
    position: {
      x: 0,
      y: 1.8,
      z: 10,
    },
    disablePan: false,
    bindToYPlaneWithHeight: false,
    distance: {
      min: 0.01,
      max: 10000,
    },
    // in deg 0<=min<=max<=180
    polarAngle: {
      min: 0,
      max: 180,
      locked: false,
    },
    //auch in deg, blockiert nicht bei Werten ausserhalb von [-180,180]
    azimuthAngle: {
      min: -Infinity,
      max: Infinity,
    },
    minCameraTargetPosition: {
      x: -Infinity,
      y: -Infinity,
      z: -Infinity,
    },
    maxCameraTargetPosition: {
      x: Infinity,
      y: Infinity,
      z: Infinity,
    },
    minCameraPosition: {
      x: -Infinity,
      y: -Infinity,
      z: -Infinity,
    },
    maxCameraPosition: {
      x: Infinity,
      y: Infinity,
      z: Infinity,
    },
  },

  connectors: {
    size: 0.1,
    anchorPlane: (Maths3 as any).plane.y0.clone(),
    anchorsAreGroundedDistance: 0.1,
    distanceWhereForceIs0: 3,
    distanceWhereForceIs1: 0.2,
    distanceWhereForcePulseIs0: 5,
    distanceWhereForcePulseIs1: 0.5,
    PulseFactor: 50,
    PulseGroudLine: 0.4,
    openingAngle: 270,
    openingAngleForce: 0,
    openingAngleWhereForceIs1: 90,
    forceShuffleSteps: 1,
    snapDistance: 0.5,

    markers: {
      alpha: 308,
      speed: 0.5,
      ab: 0.22,
      tori: 2,
      radius: 0.1,
      tube: 0.02,
      radialSegments: 3,
      tubularSegments: 16,
    },

    box: {
      animate: true,
      minDistanceTreshold: 0.3,
    },
  },

  shadows: {
    enabled: true,
    shadowMapType: "PCFSoftShadowMap",
  },

  printDesigner: {
    hideControls: false,
    font: {
      scale: 2.0,
      color: "#000000",
    },

    qualityThreshold: {
      lowDPI: 75,
      highDPI: 150,
    },
  },

  saoPass: {
    saoBias: -0.68,
    saoIntensity: 0.0005,
    saoScale: 12, //6
    saoKernelRadius: 39,
    saoMinResolution: 0,
    saoBlur: true,
    saoBlurRadius: 8, //6
    saoBlurStdDev: 3, //2
    saoBlurDepthCutoff: 0.002,
  },

  rendererBackgroundImage: false,

  lightSettingsName: "default",
};
